import { dash, first, uid } from 'radash';

import { publicConfig } from '@public-config';

import { FilePayload } from 'models';
import { FileHelpers } from 'helpers/FileHelpers';

import type { File as PayloadFile } from '@/payload-types';

import { ApiService } from './ApiService';
import { PayloadSDK } from './PayloadSDK';

// hacking this because this whole "client upload" idea is a hack
const FILE_PREFIX = publicConfig.environment === 'production' ? 'dcide-production' : 'dcide-staging';

const FileService = {
    list: async () => {
        return await ApiService.get(`${publicConfig.urls.api}/files`);
    },

    create: async (payload: { file: File; name?: string; group: string }) => {
        const { file, name, group } = payload;

        const cleanName = name || first(file.name.split('.'))!;
        const extension = getUploadExtension(file);
        const uniqueName = extension ? `${dash(cleanName)}-${uid(6)}.${extension}` : `${dash(cleanName)}-${uid(6)}`;

        const fileToUpload = new File([file], uniqueName, { type: file.type });

        return await determineFileCreationStrategy(cleanName, group, fileToUpload);
    },

    createFromUrl: async (url: string, name: string, group: string) => {
        const params = [`url=${encodeURIComponent(url)}`, `name=${name}`, `group=${group}`].join('&');

        return await ApiService.get<{
            success: boolean;
            id: string;
        }>(`${publicConfig.urls.api}/files/create-from-url?${params}`);
    },

    get: async (fileId: string) => {
        return ApiService.get(`${publicConfig.urls.api}/files/${fileId}`);
    },

    update: async (fileId: string, payload: any) => {
        return await ApiService.patch(`${publicConfig.urls.api}/files/${fileId}`, payload);
    },

    delete: async (fileId: string) => {
        return await ApiService.delete(`${publicConfig.urls.api}/files/${fileId}`);
    },

    addFile: async ({
        file,
        name,
        group,
        ...payload
    }: { file: File; name?: string; group: string } & Omit<FilePayload, 'file' | 'url'>) => {
        const newFile = await FileService.create({ file, name, group });

        const updatedCollection = await ApiService.post(`${publicConfig.urls.api}/add-file`, {
            file: newFile.id,
            ...payload,
        });

        return { newFile, updatedCollection };
    },

    addUrl: async (payload: Omit<FilePayload, 'file'> & { url: string }) => {
        return await ApiService.post(`${publicConfig.urls.api}/add-file`, payload);
    },

    deleteFile: async (payload: Omit<FilePayload, 'type'>) => {
        return await ApiService.post(`${publicConfig.urls.api}/delete-file`, payload);
    },

    getNameAndExtension: (file: File) => {
        const parts = file.name.split('.');
        const extension = parts.pop();
        const name = parts.join('.');

        return { name, extension };
    },

    download: async (source: string | Blob, filename: string) => {
        const blob = source instanceof Blob ? source : await fetchBlob(source);
        const objectUrl = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();

        URL.revokeObjectURL(objectUrl);
    },
};

/**
 * The HTML download attribute is ignored for cross-origin URLs
 * (our files live on a separate CDN), so fetch the bytes and trigger the download from a same-origin Object URL.
 */
const fetchBlob = async (url: string) => {
    const response = await fetch(url);

    return await response.blob();
};

const getUploadExtension = (file: File): string => {
    const fromMimeType = FileHelpers.extension(file.type).replace(/^\./, '');
    if (fromMimeType) {
        return fromMimeType;
    }

    const lastDot = file.name.lastIndexOf('.');

    return lastDot > 0 ? file.name.slice(lastDot + 1) : '';
};

const getPresignedUrl = async (file: File) => {
    const { url } = await ApiService.post<{ url: string }>(`${publicConfig.urls.api}/storage-s3-generate-signed-url`, {
        collectionSlug: 'files',
        filename: file.name,
        filesize: file.size,
        mimetype: file.type,
    });

    return url;
};

type FileCreateFunction = (name: string, group: string, fileToUpload: File) => Promise<PayloadFile>;

const createFileDirectly: FileCreateFunction = async (name, group, fileToUpload) => {
    return await PayloadSDK.create({
        collection: 'files',
        data: {
            name,
            group,
        },
        file: fileToUpload,
    });
};

const createFileWithPresignUrl: FileCreateFunction = async (name, group, fileToUpload) => {
    const presignedUrl = await getPresignedUrl(fileToUpload);

    const uploadResponse = await fetch(presignedUrl, {
        body: fileToUpload,
        headers: {
            'Content-Length': fileToUpload.size.toString(),
            'Content-Type': fileToUpload.type,
        },
        method: 'PUT',
    });

    if (!uploadResponse.ok) {
        throw new Error('Failed to upload file');
    }

    const formData = new FormData();

    formData.append(
        '_payload',
        JSON.stringify({
            name,
            group,
            prefix: FILE_PREFIX,
            subfolder: '/',
        }),
    );

    formData.append(
        'file',
        JSON.stringify({
            clientUploadContext: {
                prefix: FILE_PREFIX,
            },
            collectionSlug: 'files',
            filename: fileToUpload.name,
            mimeType: fileToUpload.type,
            size: fileToUpload.size,
        }),
    );

    const response = await fetch(`${publicConfig.urls.api}/files`, {
        method: 'POST',
        body: formData,
    });

    if (!response.ok) {
        throw new Error('Failed to create file');
    }
    const responseData = (await response.json()) as { doc: PayloadFile; message: string };

    return responseData.doc as PayloadFile;
};

const determineFileCreationStrategy =
    publicConfig.environment === 'development' || publicConfig.environment === 'local'
        ? createFileDirectly
        : createFileWithPresignUrl;

export { FileService };
